import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import {
  WORKFLOW_DISPLAY_DEFAULTS,
  buildWorkflowDisplayDraft,
} from "../../../../constants/workflowDisplayDefaults";
import {
  hasAnyDuplicateFinanceCodes,
  installFinanceCodeStarterPack,
  normalizeFinanceCodes,
  saveFinanceCodes,
} from "../../../../services/financeCodes";
import FinanceCodesSettingsPanel from "./FinanceCodesSettingsPanel";

const defaultDraft = {
  defaultInvoiceView: "default",
  defaultCurrency: "USD",
  defaultPresentationView: "default",
  defaultTaxId: "",
  defaultTermsId: "",
  defaultAsiMarket: "CAN",
  defaultMargin: "",
  defaultPoDate: "shipping",
  assignedUsersView: "default",
};

const invoiceViews = [{ id: "default", name: "Default" }];
const presentationViews = [{ id: "default", name: "Default" }];
const currencies = [
  { id: "USD", name: "USD" },
  { id: "CAD", name: "CAD" },
];
const poDateOptions = [
  { id: "shipping", name: "Next Action Date" },
  { id: "in-hands", name: "In Hands Date" },
];
const asiMarketOptions = [
  { id: "CAN", name: "Canada" },
  { id: "USA", name: "United States" },
];
const assignedUsersViewOptions = [
  { id: "default", name: "Default" },
  { id: "grouped", name: "Grouped by Role" },
];

export default function DefaultsTab({ instanceUniqueId }) {
  const [draft, setDraft] = useState(defaultDraft);
  const [workflowDraft, setWorkflowDraft] = useState(buildWorkflowDisplayDraft());
  const [saving, setSaving] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [termsOptions, setTermsOptions] = useState([]);
  const [instanceId, setInstanceId] = useState(null);
  const [selectedTab, setSelectedTab] = useState("general");
  const [financeCodes, setFinanceCodes] = useState([]);
  const [financeCodesMessage, setFinanceCodesMessage] = useState("");

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchDefaults = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/defaults`,
      headers
    );
    const defaults = res.data?.defaults || {};
    setDraft({
      defaultInvoiceView: defaults.defaultInvoiceView ?? "default",
      defaultCurrency: defaults.defaultCurrency ?? "USD",
      defaultPresentationView: defaults.defaultPresentationView ?? "default",
      defaultTaxId: defaults.defaultTaxId ?? "",
      defaultTermsId: defaults.defaultTermsId ?? "",
      defaultAsiMarket: defaults.defaultAsiMarket ?? "CAN",
      defaultMargin: defaults.defaultMargin ?? "",
      defaultPoDate: defaults.defaultPoDate ?? "shipping",
      assignedUsersView: defaults.assignedUsersView === "grouped" ? "grouped" : "default",
    });
    setWorkflowDraft(buildWorkflowDisplayDraft(defaults.workflowDisplaySettings));
    setFinanceCodes(normalizeFinanceCodes(defaults.financeCodes || []));
  };

  const fetchTaxes = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/taxes`,
      headers
    );
    setTaxes(res.data.taxes || []);
  };

  const fetchTerms = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/terms`,
      headers
    );
    setTermsOptions(res.data.terms || []);
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) {
      fetchDefaults(instanceId);
      fetchTaxes(instanceId);
      fetchTerms(instanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const updateWorkflowCell = (group, statusKey, field, value) => {
    setWorkflowDraft((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [statusKey]: {
          ...(prev[group]?.[statusKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  const resetWorkflowRowToDefault = (group, row) => {
    updateWorkflowCell(group, row.statusKey, "displayLabel", row.displayLabel);
    updateWorkflowCell(group, row.statusKey, "tooltip", row.tooltip);
  };

  const save = async () => {
    if (!instanceId) return;
    setSaving(true);
    setFinanceCodesMessage("");
    try {
      const response = await axios.put(
        `${SERVER}/instances/${instanceId}/defaults`,
        {
          defaultInvoiceView: draft.defaultInvoiceView || null,
          defaultCurrency: draft.defaultCurrency || null,
          defaultPresentationView: draft.defaultPresentationView || null,
          defaultTaxId: draft.defaultTaxId ? Number(draft.defaultTaxId) : null,
          defaultTermsId: draft.defaultTermsId ? Number(draft.defaultTermsId) : null,
          defaultAsiMarket: draft.defaultAsiMarket || null,
          defaultMargin: draft.defaultMargin === "" ? null : Number(draft.defaultMargin),
          defaultPoDate: draft.defaultPoDate || null,
          assignedUsersView: draft.assignedUsersView === "grouped" ? "grouped" : "default",
          workflowDisplaySettings: workflowDraft,
        },
        headers
      );
      setWorkflowDraft(buildWorkflowDisplayDraft(response.data?.defaults?.workflowDisplaySettings));
      await saveFinanceCodes(instanceUniqueId, financeCodes);
      await fetchDefaults(instanceId);
      setFinanceCodesMessage("Finance codes saved.");
    } finally {
      setSaving(false);
    }
  };

  const installStarterPack = async () => {
    setSaving(true);
    setFinanceCodesMessage("");
    try {
      const result = await installFinanceCodeStarterPack(instanceUniqueId, "billing");
      setFinanceCodes(result.financeCodes);
      setFinanceCodesMessage(`Billing Starter Pack installed: ${result.added} added, ${result.skipped} skipped.`);
    } finally {
      setSaving(false);
    }
  };

  const canSave = useMemo(() => {
    const hasValidMargin = draft.defaultMargin === "" || Number.isFinite(Number(draft.defaultMargin));
    for (const row of financeCodes || []) {
      const key = String(row?.code || "").trim();
      if (!key) return false;
    }
    return hasValidMargin && !hasAnyDuplicateFinanceCodes(financeCodes);
  }, [draft, financeCodes]);

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Defaults
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Set defaults used throughout this instance. Workflows lets you customize display labels/tooltips while keeping canonical system statuses.
        </Typography>
      </Box>

      <Tabs value={selectedTab} onChange={(_, next) => setSelectedTab(next)}>
        <Tab label="General" value="general" />
        <Tab label="Workflows" value="workflows" />
        <Tab label="Finance Codes" value="financeCodes" />
      </Tabs>

      {selectedTab === "general" && (
        <Box sx={{ display: "grid", gap: 2 }}>
          <TextField
            label="Default Invoice View"
            select
            value={draft.defaultInvoiceView}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultInvoiceView: e.target.value }))}
          >
            {invoiceViews.map((view) => (
              <MenuItem key={view.id} value={view.id}>
                {view.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Default Currency"
            select
            value={draft.defaultCurrency}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultCurrency: e.target.value }))}
          >
            {currencies.map((currency) => (
              <MenuItem key={currency.id} value={currency.id}>
                {currency.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Default Presentation View"
            select
            value={draft.defaultPresentationView}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, defaultPresentationView: e.target.value }))
            }
          >
            {presentationViews.map((view) => (
              <MenuItem key={view.id} value={view.id}>
                {view.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Default Tax"
            select
            value={draft.defaultTaxId}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultTaxId: e.target.value }))}
          >
            <MenuItem value="">None</MenuItem>
            {(taxes || []).map((tax) => (
              <MenuItem key={tax.taxId} value={tax.taxId}>
                {tax.name || [tax.stateCode || tax.countryCode, tax.type].filter(Boolean).join(" + ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Default Terms"
            select
            value={draft.defaultTermsId}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultTermsId: e.target.value }))}
          >
            <MenuItem value="">None</MenuItem>
            {(termsOptions || []).map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="ASI Product Search - Default Location"
            select
            value={draft.defaultAsiMarket}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultAsiMarket: e.target.value }))}
          >
            {asiMarketOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Default Margin (%)"
            type="number"
            value={draft.defaultMargin}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultMargin: e.target.value }))}
          />

          <TextField
            label="Default PO Date"
            select
            value={draft.defaultPoDate}
            onChange={(e) => setDraft((prev) => ({ ...prev, defaultPoDate: e.target.value }))}
          >
            {poDateOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Assigned Users View"
            select
            value={draft.assignedUsersView}
            onChange={(e) => setDraft((prev) => ({ ...prev, assignedUsersView: e.target.value }))}
          >
            {assignedUsersViewOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {selectedTab === "workflows" && (
        <Box sx={{ display: "grid", gap: 2 }}>
          {Object.entries(WORKFLOW_DISPLAY_DEFAULTS).map(([group, rows]) => (
            <Box key={group} sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 2, display: "grid", gap: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {group === "SO" ? "Sales Order" : group === "PO" ? "Purchase Order" : "Global"} Workflows
              </Typography>

              {rows.map((row) => {
                const current = workflowDraft?.[group]?.[row.statusKey] || {
                  displayLabel: row.displayLabel,
                  tooltip: row.tooltip,
                };
                return (
                  <Box
                    key={`${group}-${row.statusKey}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "220px 1fr 1fr auto" },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.statusKey}
                    </Typography>
                    <TextField
                      size="small"
                      label="Display label"
                      value={current.displayLabel || ""}
                      onChange={(e) => updateWorkflowCell(group, row.statusKey, "displayLabel", e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Tooltip"
                      value={current.tooltip || ""}
                      onChange={(e) => updateWorkflowCell(group, row.statusKey, "tooltip", e.target.value)}
                    />
                    <Button
                      variant="text"
                      onClick={() => resetWorkflowRowToDefault(group, row)}
                    >
                      Reset
                    </Button>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      {selectedTab === "financeCodes" && (
        <FinanceCodesSettingsPanel
          financeCodes={financeCodes}
          setFinanceCodes={setFinanceCodes}
          onInstallStarterPack={installStarterPack}
          feedbackMessage={financeCodesMessage}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={save} disabled={!canSave || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
